import { useEffect, useRef, useState } from 'react';
import { useGame } from '../store/game';
import { useOnline } from '../store/online';
import { useUi } from '../store/ui';
import { compareRolls } from '@estandarte/engine';
import { crestOf } from '../art';
import { guildHex, guildName, missionInfo, S, theme, vocationName } from '../theme';
import { CardBack } from './Cards';

/** Foca o primeiro botão ao abrir e fecha com Esc (quando há onClose). */
function useDialog(onClose?: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return ref;
}

export function PassScreen({ player }: { player: number }) {
  const { view, confirmPass } = useGame();
  const ref = useDialog();
  if (!view) return null;
  const p = view.players[player];
  const pending = view.pending?.player === player && view.pending.kind === 'bossRegion';
  return (
    <div className="overlay solid" role="dialog" aria-modal="true" aria-labelledby="pass-title">
      <div className="modal pass-card" ref={ref}>
        <img src={crestOf(p.color)} width={84} height={84} alt="" style={{ display: 'block', margin: '0 auto 0.5rem' }} />
        <p className="sc muted" style={{ margin: 0 }}>
          {guildName(p.color)}
        </p>
        <h2 id="pass-title">Vez de {p.name}</h2>
        <p className="muted">
          {pending
            ? 'Como mestre dos bosses, você escolhe onde o próximo boss surge.'
            : 'Passe o computador. Os outros não devem olhar a tela.'}
        </p>
        <button className="btn primary" onClick={confirmPass} style={{ background: guildHex(p.color), borderColor: guildHex(p.color) }}>
          Sou eu, {p.name}
        </button>
      </div>
    </div>
  );
}

export function MissionModal() {
  const { view, me } = useGame();
  const close = () => useUi.getState().toggleMission(false);
  const ref = useDialog(close);
  if (!view || me === null) return null;
  const mission = view.players[me].mission;
  const info = mission ? missionInfo(theme, mission) : null;
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="mission-title" onClick={close}>
      <div className="modal mission-card" ref={ref} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, textAlign: 'center' }}>
        <CardBack deck="mission" />
        <p className="sc muted" style={{ margin: '0.5rem 0 0' }}>
          Missão secreta de {view.players[me].name}
        </p>
        <h2 id="mission-title" style={{ fontSize: '1.9rem', margin: '0.25rem 0 0.5rem' }}>
          {info?.name ?? '—'}
        </h2>
        <p style={{ fontSize: '1.1rem', margin: '0 0 1rem' }}>{info?.text}</p>
        <p className="hint">
          Cumpra a missão para vencer na hora. Se ninguém cumprir até o fim da rodada {view.info.ruleset.serverSaveRound}, vence a maior
          pontuação (Server Save).
        </p>
        <button className="btn" onClick={close}>
          Fechar
        </button>
      </div>
    </div>
  );
}

export function EndScreen() {
  const { view, mode, leaveGame } = useGame();
  const ref = useDialog();
  if (!view?.result) return null;
  const r = view.result;
  const winner = view.players[r.winner];
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="end-title">
      <div className="modal" ref={ref} style={{ maxWidth: 680 }}>
        <div className="row" style={{ gap: '1rem', alignItems: 'center' }}>
          <img src={crestOf(winner.color)} width={72} height={72} alt={`Brasão de ${guildName(winner.color)}`} />
          <div>
            <p className="sc muted" style={{ margin: 0 }}>
              {S.scoreReasons[r.reason]}
            </p>
            <h2 id="end-title" style={{ fontSize: '2.2rem' }}>
              Vitória de {winner.name}
            </h2>
            <p className="muted" style={{ margin: 0 }}>
              {guildName(winner.color)}
            </p>
          </div>
        </div>
        <table className="scores">
          <thead>
            <tr>
              <th>Guilda</th>
              <th>Missão</th>
              <th className="n">Terr.</th>
              <th className="n">Reg.</th>
              <th className="n">Boss</th>
              <th className="n">Tropas</th>
              <th className="n">Pontos</th>
            </tr>
          </thead>
          <tbody>
            {r.scores.map((l) => {
              const p = view.players[l.player];
              return (
                <tr key={l.player} className={l.player === r.winner ? 'winner' : ''}>
                  <td>
                    <img src={crestOf(p.color)} width={20} height={20} alt="" style={{ verticalAlign: 'middle' }} /> {p.name}
                    <div className="hint">
                      {vocationName(p.vocation)}
                      {p.alive ? '' : ' · eliminado'}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {p.mission ? (
                      <>
                        <strong>{missionInfo(theme, p.mission).name}</strong>
                        <div className="hint">{missionInfo(theme, p.mission).text}</div>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="n">{l.territories}</td>
                  <td className="n">{l.regions}</td>
                  <td className="n">{l.bossKills}</td>
                  <td className="n">{l.troops}</td>
                  <td className="n">{l.score}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="hint">Pontuação do Server Save: 1 por território, 3 por região completa, 2 por boss morto. Desempate por tropas.</p>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn primary" onClick={() => (mode === 'online' ? useOnline.getState().leave() : leaveGame())}>
            Voltar ao início
          </button>
        </div>
      </div>
    </div>
  );
}

/** Janela do Amuleto da Sorte: rerrolar os próprios dados desta rolagem? */
export function ReactionPrompt() {
  const { reaction, view, dispatch, mode } = useGame();
  const ref = useDialog();
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (mode !== 'online' || !reaction) return;
    const end = Date.now() + 10_000;
    setLeft(10);
    const t = setInterval(() => setLeft(Math.max(0, Math.ceil((end - Date.now()) / 1000))), 250);
    return () => clearInterval(t);
  }, [reaction, mode]);
  if (!reaction || !view) return null;
  const p = view.players[reaction.player];
  const c = reaction.combat;
  const out = compareRolls(c.attackRolls, c.defenseRolls, c.tieGoesTo, c.defenderWinsAll);
  const mineLoss = reaction.role === 'attacker' ? out.attackerLoss : out.defenderLoss;
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="react-title">
      <div className="modal" ref={ref} style={{ maxWidth: 440, textAlign: 'center' }}>
        <p className="sc muted" style={{ margin: 0 }}>
          Decisão de {p.name} ({reaction.role === 'attacker' ? 'atacante' : 'defensor'})
        </p>
        <h2 id="react-title" style={{ fontSize: '1.8rem' }}>
          Usar {theme.loot.luck.name}?
        </h2>
        <p>
          Ataque [{c.attackRolls.join(' ')}] × defesa [{c.defenseRolls.join(' ')}]. Você perderia <strong>{mineLoss}</strong> tropa(s). O
          amuleto rerrola todos os seus dados desta rolagem.
        </p>
        {left !== null && <p className="hint">Sem resposta em {left} s, segue sem usar.</p>}
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="btn primary" onClick={() => dispatch({ type: 'react', player: reaction.player, use: true })}>
            Usar o amuleto
          </button>
          <button className="btn" onClick={() => dispatch({ type: 'react', player: reaction.player, use: false })}>
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
